import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import moment from 'moment';
import {Confirm, Loading, Notify} from 'notiflix';
import {
  Card,
  CircularProgress,
  Grid,
  Divider,
  Icon,
  IconButton,
  TextField,
  MenuItem,
  Tooltip,
  Box,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {envVariablesApi} from 'api/envVariablesApi';
import useDebounce from 'hooks/useDebounce';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';
import {CATEGORY_OPTIONS, categoryLabel} from './categoryLabels';

const APP_OPTIONS = [
  {value: 'backend', label: 'Backend'},
  {value: 'frontend', label: 'Frontend'},
  {value: 'admin-dashboard', label: 'Admin Dashboard'},
];

const ENVIRONMENT_OPTIONS = [
  {value: 'development', label: 'Development'},
  {value: 'stage', label: 'Stage'},
  {value: 'production', label: 'Production'},
];

const KEY_PATTERN = /^[A-Z][A-Z0-9_]*$/;

const REVEAL_DURATION_MS = 10000;

const selectSx = {height: 44};

const appLabel = value => APP_OPTIONS.find(a => a.value === value)?.label || value;

const EMPTY_NEW_FORM = {
  key: '',
  category: 'custom',
  description: '',
  isSecret: true,
  value: '',
};

const EnvVariables = () => {
  const {user} = useSelector(state => state.auth);
  const canRead = usePermission('env-variables', 'read');
  const canCreate = usePermission('env-variables', 'create');
  const canUpdate = usePermission('env-variables', 'update');
  const canReveal = usePermission('env-variables', 'reveal');
  const canLock = usePermission('env-variables', 'lock');

  const [app, setApp] = useState('');
  const [environment, setEnvironment] = useState('');

  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);

  // id -> temporarily revealed plaintext value
  const [revealed, setRevealed] = useState({});
  const revealTimersRef = useRef({});

  // Inline edit state: id of the row being edited + its draft value
  const [editingId, setEditingId] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [saving, setSaving] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newVar, setNewVar] = useState(EMPTY_NEW_FORM);
  const [creating, setCreating] = useState(false);

  const canLoad = Boolean(app) && Boolean(environment) && canRead;

  const fetchVariables = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    setError(null);
    try {
      const params = {app, environment};
      if (category) params.category = category;
      if (debouncedSearch) params.search = debouncedSearch;
      const data = await envVariablesApi.fetchVariables(user?.accessToken, params);
      setVariables(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching env variables:', err);
      setError(err?.response?.data?.message || 'Failed to load environment variables');
    } finally {
      setLoading(false);
    }
  }, [app, environment, category, debouncedSearch, canLoad, user?.accessToken, canRead]);

  useEffect(() => {
    fetchVariables();
  }, [fetchVariables]);

  const clearRevealTimers = () => {
    Object.values(revealTimersRef.current).forEach(clearTimeout);
    revealTimersRef.current = {};
  };

  // Clear any pending reveal timers and edit state whenever the selected app/environment changes
  useEffect(() => {
    clearRevealTimers();
    setRevealed({});
    setEditingId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, environment]);

  // Clear any pending reveal timers when the page unmounts
  useEffect(() => clearRevealTimers, []);

  const grouped = useMemo(() => {
    const groups = {};
    variables.forEach(v => {
      const key = v.category || 'custom';
      if (!groups[key]) groups[key] = [];
      groups[key].push(v);
    });
    return Object.entries(groups).sort(([a], [b]) => categoryLabel(a).localeCompare(categoryLabel(b)));
  }, [variables]);

  const startEdit = row => {
    if (row.isLocked) return;
    setEditingId(row.id);
    setDraftValue(row.isSecret ? '' : row.currentValue || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftValue('');
  };

  const handleSaveValue = async row => {
    setSaving(true);
    try {
      await envVariablesApi.updateValue(user?.accessToken, row.id, draftValue);
      Notify.success(
        `Saved. Changes take effect after the ${appLabel(app)} is manually restarted/redeployed — this does not happen automatically.`,
      );
      setEditingId(null);
      setDraftValue('');
      fetchVariables();
    } catch (err) {
      Notify.failure(err?.response?.data?.message || 'Failed to save value');
    } finally {
      setSaving(false);
    }
  };

  const handleReveal = async row => {
    try {
      const data = await envVariablesApi.reveal(user?.accessToken, row.id);
      const plaintext = data?.currentValue ?? '';
      if (revealTimersRef.current[row.id]) clearTimeout(revealTimersRef.current[row.id]);
      revealTimersRef.current[row.id] = setTimeout(() => {
        setRevealed(prev => {
          const next = {...prev};
          delete next[row.id];
          return next;
        });
        delete revealTimersRef.current[row.id];
      }, REVEAL_DURATION_MS);
      setRevealed(prev => ({...prev, [row.id]: plaintext}));
    } catch (err) {
      Notify.failure(err?.response?.data?.message || 'Failed to reveal value');
    }
  };

  const handleToggleLock = row => {
    const nextLocked = !row.isLocked;
    Confirm.show(
      nextLocked ? 'Lock this variable?' : 'Unlock this variable?',
      nextLocked
        ? 'Locking prevents this from being edited from the panel until unlocked again. Continue?'
        : 'This variable will become editable from the panel again. Continue?',
      nextLocked ? 'Lock' : 'Unlock',
      'Cancel',
      async () => {
        try {
          Loading.circle(nextLocked ? 'Locking...' : 'Unlocking...');
          await envVariablesApi.setLocked(user?.accessToken, row.id, nextLocked);
          Loading.remove();
          Notify.success(nextLocked ? 'Variable locked' : 'Variable unlocked');
          fetchVariables();
        } catch (err) {
          Loading.remove();
          Notify.failure(err?.response?.data?.message || 'Failed to update lock state');
        }
      },
      () => {},
    );
  };

  const openAddDialog = () => {
    setNewVar(EMPTY_NEW_FORM);
    setAddDialogOpen(true);
  };

  const validateNewVar = () => {
    const key = newVar.key.trim().toUpperCase();
    if (!key) return 'Key is required';
    if (!KEY_PATTERN.test(key)) return 'Key must be UPPER_SNAKE_CASE (letters, numbers, underscores)';
    return null;
  };

  const handleCreateVar = async () => {
    const validationError = validateNewVar();
    if (validationError) {
      Notify.warning(validationError);
      return;
    }
    setCreating(true);
    try {
      await envVariablesApi.createVariable(user?.accessToken, {
        key: newVar.key.trim().toUpperCase(),
        app,
        environment,
        category: newVar.category || 'custom',
        description: newVar.description.trim() || undefined,
        isSecret: newVar.isSecret,
        value: newVar.value || undefined,
      });
      Notify.success('Variable created');
      setAddDialogOpen(false);
      fetchVariables();
    } catch (err) {
      const message =
        err?.response?.status === 409
          ? err?.response?.data?.message || 'This key already exists for this app and environment'
          : err?.response?.data?.message || 'Failed to create variable';
      Notify.failure(message);
    } finally {
      setCreating(false);
    }
  };

  const maskedDisplay = row => {
    if (revealed[row.id] !== undefined) return revealed[row.id];
    if (!row.hasOverride) return '—';
    return row.currentValue || '—';
  };

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to Environment Variables. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} sx={{display: 'flex'}}>
            <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h5" fontWeight="medium">
                    Environment Variables
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    Single source of truth for Backend / Frontend / Admin Dashboard env values. Saving a value
                    here does <strong>not</strong> restart or redeploy anything — the running app keeps using
                    whatever it already loaded until it is manually restarted/redeployed.
                  </MDTypography>

                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        select
                        fullWidth
                        label="App"
                        value={app}
                        onChange={e => setApp(e.target.value)}
                        SelectProps={{sx: selectSx}}>
                        {APP_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        select
                        fullWidth
                        label="Environment"
                        value={environment}
                        onChange={e => setEnvironment(e.target.value)}
                        SelectProps={{sx: selectSx}}>
                        {ENVIRONMENT_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </MDBox>

                {!canLoad && (
                  <MDBox textAlign="center" py={6} px={3}>
                    <Icon sx={{fontSize: 56, color: 'text.secondary', mb: 2}}>tune</Icon>
                    <MDTypography variant="h6" mb={1}>
                      Select an App and Environment to view its variables
                    </MDTypography>
                  </MDBox>
                )}

                {canLoad && (
                  <>
                    <Divider sx={{my: 0}} />
                    <MDBox
                      p={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={2}>
                      <MDBox display="flex" gap={2} flexWrap="wrap" flex={1}>
                        <Box minWidth={220}>
                          <MDInput
                            fullWidth
                            label="Search key or description"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                          />
                        </Box>
                        <Box minWidth={180}>
                          <TextField
                            select
                            fullWidth
                            label="Category"
                            value={category}
                            onChange={e => setCategory(e.target.value)}
                            SelectProps={{sx: selectSx}}>
                            <MenuItem value="">All Categories</MenuItem>
                            {CATEGORY_OPTIONS.map(c => (
                              <MenuItem key={c} value={c}>
                                {categoryLabel(c)}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      </MDBox>
                      <MDBox display="flex" gap={1}>
                        <MDButton variant="outlined" color="info" onClick={() => fetchVariables()}>
                          <Icon sx={{mr: 0.5}}>refresh</Icon>
                          Refresh
                        </MDButton>
                        {canCreate && (
                          <MDButton variant="contained" color="info" onClick={openAddDialog}>
                            <Icon sx={{mr: 0.5}}>add</Icon>
                            Add New Variable
                          </MDButton>
                        )}
                      </MDBox>
                    </MDBox>

                    {loading && (
                      <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                        <CircularProgress size="2.5rem" color="inherit" />
                      </MDBox>
                    )}

                    {!loading && error && (
                      <MDBox textAlign="center" py={6} px={3}>
                        <Icon sx={{fontSize: 48, color: 'error.main', mb: 2}}>error_outline</Icon>
                        <MDTypography variant="h6" mb={2}>
                          {error}
                        </MDTypography>
                        <MDButton variant="contained" onClick={() => fetchVariables()}>
                          Retry
                        </MDButton>
                      </MDBox>
                    )}

                    {!loading && !error && variables.length === 0 && (
                      <MDBox textAlign="center" py={6}>
                        <Icon sx={{fontSize: 56, color: 'text.secondary', mb: 2}}>key_off</Icon>
                        <MDTypography variant="h6">No variables found for this selection</MDTypography>
                      </MDBox>
                    )}

                    {!loading &&
                      !error &&
                      grouped.map(([cat, rows]) => (
                        <MDBox key={cat} px={3} pb={3}>
                          <MDTypography
                            variant="subtitle2"
                            color="text"
                            fontWeight="bold"
                            textTransform="uppercase"
                            sx={{mt: 3, mb: 1, display: 'block'}}>
                            {categoryLabel(cat)}
                          </MDTypography>
                          <Divider sx={{mt: 0, mb: 1}} />
                          {rows.map(row => (
                            <MDBox
                              key={row.id}
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 2,
                                py: 1.5,
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                              }}>
                              <MDBox sx={{minWidth: 220, flex: '1 1 220px'}}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                  sx={{fontFamily: 'monospace', wordBreak: 'break-all'}}>
                                  {row.key}
                                </MDTypography>
                                {row.description && (
                                  <MDTypography variant="caption" color="text" display="block">
                                    {row.description}
                                  </MDTypography>
                                )}
                              </MDBox>

                              <MDBox sx={{minWidth: 200, flex: '2 1 260px'}}>
                                {editingId === row.id ? (
                                  <MDBox display="flex" gap={1} alignItems="center">
                                    <MDInput
                                      fullWidth
                                      size="small"
                                      autoFocus
                                      type={row.isSecret ? 'password' : 'text'}
                                      placeholder={row.isSecret ? 'New value' : 'Value'}
                                      value={draftValue}
                                      onChange={e => setDraftValue(e.target.value)}
                                    />
                                    <Tooltip title="Save">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="success"
                                          disabled={saving}
                                          onClick={() => handleSaveValue(row)}>
                                          <Icon fontSize="small">check</Icon>
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                      <span>
                                        <IconButton size="small" disabled={saving} onClick={cancelEdit}>
                                          <Icon fontSize="small">close</Icon>
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </MDBox>
                                ) : (
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        fontFamily: 'monospace',
                                        wordBreak: 'break-all',
                                        color: row.hasOverride ? 'text.primary' : 'text.secondary',
                                      }}>
                                      {maskedDisplay(row)}
                                    </MDTypography>
                                    {row.isSecret && row.hasOverride && !revealed[row.id] && canReveal && (
                                      <Tooltip title="Reveal for 10 seconds">
                                        <IconButton size="small" onClick={() => handleReveal(row)}>
                                          <Icon fontSize="small">visibility</Icon>
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </MDBox>
                                )}
                              </MDBox>

                              <MDBadge
                                badgeContent={row.source === 'database' ? 'From Database' : 'From .env file'}
                                color={row.source === 'database' ? 'success' : 'secondary'}
                                variant="gradient"
                                size="sm"
                              />

                              {row.isLocked && (
                                <Tooltip title="This is a bootstrap-critical variable — can only be changed by editing the .env file directly on the server.">
                                  <MDBox display="flex" alignItems="center" gap={0.5}>
                                    <Icon fontSize="small" color="warning">
                                      lock
                                    </Icon>
                                    <MDTypography variant="caption" color="text">
                                      Locked
                                    </MDTypography>
                                  </MDBox>
                                </Tooltip>
                              )}

                              {row.hasOverride && row.updatedAt && (
                                <Tooltip title={moment(row.updatedAt).format('DD MMM YYYY, HH:mm:ss')}>
                                  <MDTypography variant="caption" color="text">
                                    Updated {moment(row.updatedAt).fromNow()}
                                  </MDTypography>
                                </Tooltip>
                              )}

                              <MDBox sx={{ml: 'auto'}} display="flex" gap={0.5}>
                                {editingId !== row.id && canUpdate && (
                                  <Tooltip
                                    title={
                                      row.isLocked
                                        ? 'This is a bootstrap-critical variable — can only be changed by editing the .env file directly on the server.'
                                        : 'Edit value'
                                    }>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={row.isLocked}
                                        onClick={() => startEdit(row)}>
                                        <Icon fontSize="small">edit</Icon>
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                {canLock && (
                                  <Tooltip title={row.isLocked ? 'Unlock' : 'Lock'}>
                                    <IconButton size="small" onClick={() => handleToggleLock(row)}>
                                      <Icon fontSize="small">{row.isLocked ? 'lock_open' : 'lock'}</Icon>
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </MDBox>
                            </MDBox>
                          ))}
                        </MDBox>
                      ))}
                  </>
                )}
              </Card>
            </Grid>
            <Divider orientation="vertical" sx={{mx: 0}} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={addDialogOpen} onClose={() => !creating && setAddDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add New Variable</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="caption" color="text" display="block" mb={2}>
            Will be registered for <strong>{appLabel(app)}</strong> /{' '}
            <strong>{ENVIRONMENT_OPTIONS.find(o => o.value === environment)?.label}</strong>
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Key"
                placeholder="NEW_VARIABLE_NAME"
                value={newVar.key}
                onChange={e => setNewVar(prev => ({...prev, key: e.target.value.toUpperCase()}))}
                helperText="UPPER_SNAKE_CASE only"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Category"
                value={newVar.category}
                onChange={e => setNewVar(prev => ({...prev, category: e.target.value}))}>
                {CATEGORY_OPTIONS.map(c => (
                  <MenuItem key={c} value={c}>
                    {categoryLabel(c)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newVar.isSecret}
                    onChange={e => setNewVar(prev => ({...prev, isSecret: e.target.checked}))}
                  />
                }
                label="Mask this value (secret)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (optional)"
                value={newVar.description}
                onChange={e => setNewVar(prev => ({...prev, description: e.target.value}))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type={newVar.isSecret ? 'password' : 'text'}
                label="Initial value (optional)"
                value={newVar.value}
                onChange={e => setNewVar(prev => ({...prev, value: e.target.value}))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" disabled={creating} onClick={() => setAddDialogOpen(false)}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" disabled={creating} onClick={handleCreateVar}>
            {creating ? 'Creating...' : 'Create'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default EnvVariables;
